
import { Component, Prop, Vue } from "vue-property-decorator";
import { API_ROUTES } from "@/utils/API";
import { API_TInvite, API_TInvite_Filter } from "@/models/cases_expert_invites";
import { SMToaster } from "@/store";
import copy from "copy-to-clipboard";

type ParamsKeyObject = {
  [key: string]: any;
};

const SMALL_FONT_SIZE = "small";

const ENTER_KEY_CODE = "Enter";

const ICONS = {
  pencil: "pencil-sharp",
};

@Component
export default class ExpertInvitesTable extends Vue {
  @Prop({ type: Array, default: () => ({}) }) readonly items?: API_TInvite[];
  @Prop({ type: Object, default: () => ({}) }) readonly params?: ParamsKeyObject;
  @Prop({ type: Object, default: () => ({}) }) readonly filters?: API_TInvite_Filter;
  @Prop({ type: Boolean, default: false }) readonly loading?: boolean;
  @Prop({ type: String, default: "" }) readonly savedFilters?: string;

  icons = ICONS;

  get tableProps() {
    const { loading, headers, items, filters, params, savedFilters } = this;

    return {
      loading,
      headers,
      items,
      filters,
      params,
      fontSize: SMALL_FONT_SIZE,
      savedFilters,
    };
  }

  options = {
    infiniteScroll: true,
    filtersApi: true,
    sortApi: true,
  };

  loadData(payload: any) {
    this.$emit("load-data", payload);
  }

  headers = [
    {
      text: this.$t("table.headers.email"),
      value: "email",
      slot: true,
    },
    {
      text: this.$t("table.headers.user_name"),
      value: "user_name",
    },
    {
      text: this.$t("table.headers.company_name"),
      value: "company_name",
      filterable: true,
    },
    {
      text: this.$t("table.headers.employer_email"),
      value: "employer_email",
    },
    {
      text: this.$t("table.headers.case_id"),
      value: "case_id",
      slot: true,
      sortable: true,
    },
    {
      text: this.$t("table.headers.created_at"),
      value: "created_at",
    },
    {
      text: this.$t("table.headers.expire_date"),
      value: "expire_date",
      slot: true,
    },
    {
      text: this.$t("table.headers.role"),
      value: "role",
      filterable: true,
    },
    {
      text: this.$t("table.headers.is_used"),
      value: "is_used",
      filterable: true,
    },
  ];

  emailToEdit = "";

  selectedEmailId = 0;

  onEmailClick(id: number, email: string) {
    this.selectedEmailId = id;

    this.emailToEdit = email;
  }

  onEmailBlur(email: string) {
    this.selectedEmailId = 0;

    this.emailToEdit = email;
  }

  async onEmailUpdate(e: KeyboardEvent, id: number) {
    if (e.key !== ENTER_KEY_CODE) return;

    try {
      await this.axios.put<string>(API_ROUTES.EXPERT_UPDATE_INVITE(id), {
        email: this.emailToEdit,
      });

      this.selectedEmailId = 0;

      SMToaster.emitSuccess({
        title: this.$t("labels.emailUpdated").toString(),
        message: "",
      });

      this.items.find((item) => item.id === id).email = this.emailToEdit;
    } catch (e) {
      SMToaster.emitError(e);
    } finally {
      this.selectedEmailId = 0;
    }
  }

  selectedExpireDateId = 0;

  onExpireDateClick(id: number) {
    this.selectedExpireDateId = id;
  }

  onDateClickOutside() {
    this.selectedExpireDateId = 0;
  }

  async onDatePick({ value, oldValue }: { value: string; oldValue: string }, id: number) {
    if (oldValue === value) return;

    try {
      await this.axios.put<string>(API_ROUTES.EXPERT_UPDATE_INVITE(id), {
        expire_date: value,
      });

      this.selectedExpireDateId = 0;

      SMToaster.emitSuccess({
        title: this.$t("labels.dateUpdated").toString(),
        message: "",
      });
    } catch (e) {
      SMToaster.emitError(e);
    }
  }

  async copyInviteLink(id: number) {
    try {
      const { data } = await this.axios.get<string>(API_ROUTES.EXPERT_GET_INVITE_LINK(id));

      if (!data) return;

      copy(data);

      SMToaster.emitSuccess({
        title: this.$t("labels.inviteLinkCopied").toString(),
        message: this.shortenString(data),
      });
    } catch (e) {
      SMToaster.emitError(e);
    }
  }

  async resendInvite(id: number) {
    try {
      await this.axios.get<string>(API_ROUTES.EXPERT_RESEND_INVITE(id));

      SMToaster.emitSuccess({
        title: this.$t("labels.inviteResent").toString(),
        message: "",
      });
    } catch (e) {
      SMToaster.emitError(e);
    }
  }

  shortenString(str: string) {
    if (!str) return;

    return str.length > 30 ? str.substring(0, 30) + "..." : str;
  }

  goToCase(caseId: number) {
    if (!caseId) return;

    const routeData = this.$router.resolve(`cases/${caseId}/dashboard/0`);

    window.open(routeData.href, "_blank");
  }

  copyToClipboard(text: string) {
    if (!text) return;

    copy(text);

    SMToaster.emitSuccess({
      title: this.$root.$tc("notification.copied").toString(),
      message: text,
    });
  }
}
